import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { MapsTokenResponse, ProviderRegistrationSearchResult, ProviderService } from "@asd/customer-portal-api";
import { useOrganization } from "@asd/customer-portal-ui";
import PublicIcon from "@mui/icons-material/Public";
import SearchIcon from "@mui/icons-material/Search";
import TableViewIcon from "@mui/icons-material/TableView";
import {
  Alert,
  alpha,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { AzureMapsContext, AzureMapsProvider } from "react-azure-maps";

import { FilterAutocomplete } from "~/components/shared";
import AddressPicker from "~/components/shared/AddressPicker";
import { useMapsContext } from "~/providers/MapsProvider";
import { ProviderRegistrationService } from "~/services";
import { getProviderForDisplay, ProviderRegistrationForDisplay, SERVICE_OPTIONS } from "~/utils/providerRegistrationHelpers";
import { getMapBoundsDistance } from "./map/mapHelpers";
import MapView from "./map/MapView";
import TableView from "./table/TableView";
import { useCareProvidersState } from "./useCareProvidersState";

export const FIND_PROVIDERS_TEXT = 'Set a Service Address and Services above, then click "Find Providers"';
export const NO_PROVIDERS_TEXT = "No providers found, try broadening your search";

const CareProvidersWrapper = (WrappedComponent: FC) => {
  const WrappedNavigation = (props: any) => {
    return (
      <AzureMapsProvider>
        <WrappedComponent {...props} />
      </AzureMapsProvider>
    );
  };

  return WrappedNavigation;
};

const CareProviders = () => {
  const theme = useTheme();

  const {
    configuration: {
      branding: { name, withTM },
    },
  } = useOrganization();
  const { getMapToken } = useMapsContext();
  const { mapRef } = useContext(AzureMapsContext);

  const {
    showDisclaimer,
    view,
    searchFilters,
    mapViewState,
    tableViewState,
    setShowDisclaimer,
    setView,
    setSearchFilters,
    setMapViewState,
    setTableViewState,
  } = useCareProvidersState();

  const [mapToken, setMapToken] = useState<MapsTokenResponse>({});
  const [hoverProvider, setHoverProvider] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const providersSearchAbortController = useRef<AbortController>(new AbortController());

  const handleSearch = useCallback(
    async (pendingSearchDistance?: number) => {
      try {
        if (providersSearchAbortController.current) {
          providersSearchAbortController.current.abort();
          providersSearchAbortController.current = new AbortController();
        }

        setIsLoading(true);

        const services = searchFilters.services.map((service) => {
          const found = SERVICE_OPTIONS.find(([, label]) => label === service);
          return found ? (found[0] as ProviderService) : (service as ProviderService);
        });

        let searchDistance = pendingSearchDistance ?? 10;
        if (!pendingSearchDistance && view === "map") {
          const mapBoundsDistance = getMapBoundsDistance(mapRef, mapViewState.location);
          if (mapBoundsDistance?.maxDistanceInMiles) {
            searchDistance = mapBoundsDistance.maxDistanceInMiles;
          }
        }

        const providerRegistrations = await ProviderRegistrationService.searchProviderRegistrations(
          {
            searchAddress: searchFilters.serviceAddress!.value,
            searchDistance,
            services,
          },
          {
            signal: providersSearchAbortController.current?.signal,
          },
        );

        const unsortedProviders: ProviderRegistrationForDisplay[] =
          providerRegistrations?.data?.items.map((providerRegistration: ProviderRegistrationSearchResult) =>
            getProviderForDisplay(providerRegistration),
          ) ?? [];
        const sortedProviders = [...unsortedProviders].sort((a, b) => {
          // 1. In-range first (distanceAway < distance)
          const aInRange = (a.registration.travelDistance ?? 0) >= (a.distance ?? 0);
          const bInRange = (b.registration.travelDistance ?? 0) >= (b.distance ?? 0);
          if (aInRange !== bInRange) {
            return aInRange ? -1 : 1;
          }

          // 2. Most matching services first
          const aMatches = a.registration.services?.filter((service: any) => services.includes(service.key ?? service)).length ?? 0;
          const bMatches = b.registration.services?.filter((service: any) => services.includes(service.key ?? service)).length ?? 0;
          if (aMatches !== bMatches) {
            return bMatches - aMatches;
          }

          // 3. Closest distance first
          return (a.distance ?? 0) - (b.distance ?? 0);
        });

        setMapViewState({
          providers: sortedProviders,
          currentSearch: {
            searchAddress: searchFilters.serviceAddress!.value,
            services,
          },
        });
      } catch {
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [searchFilters, view, setMapViewState, mapRef, mapViewState.location],
  );

  const mapView = useMemo(() => {
    return !mapToken.clientId ? null : (
      <MapView
        handleSearch={handleSearch}
        hasCurrentSearch={!!mapViewState.currentSearch}
        hoverProvider={hoverProvider}
        mapToken={mapToken}
        mapViewState={mapViewState}
        setHoverProvider={setHoverProvider}
        setIsLoading={setIsLoading}
        setMapViewState={setMapViewState}
      />
    );
  }, [handleSearch, hoverProvider, mapToken, mapViewState, setHoverProvider, setMapViewState]);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const _mapToken = await getMapToken();
        setMapToken(_mapToken);
      } catch {
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    })();

    return () => {
      providersSearchAbortController.current?.abort();
      providersSearchAbortController.current = new AbortController();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setHoverProvider(null);
  }, [searchFilters]);

  return (
    <Stack
      direction="column"
      sx={{
        p: 1,
        height: "100%",
        width: "100%",
        justifyContent: "center",
        alignItems: "center",
        overflow: "auto",
      }}
    >
      {showDisclaimer && (
        <Alert onClose={() => setShowDisclaimer(false)} severity="warning" sx={{ mb: 1, fontStyle: "italic" }}>
          DISCLAIMER: The {name}
          {withTM && (
            <Typography component="sup" fontSize="xx-small" variant="caption">
              TM
            </Typography>
          )}{" "}
          Provider Directory is intended to help connect our program participants/members to direct care professionals and vendors in their
          area. {name}
          {withTM && (
            <Typography component="sup" fontSize="xx-small" variant="caption">
              TM
            </Typography>
          )}{" "}
          disclaims any responsibility for the accuracy, reliability, or suitability of the information contained herein. Inclusion in the
          Provider Directory does not constitute an endorsement of any kind or warranty of suitability for any provider or vendor contained
          herein. All service providers are required to successfully complete the enrollment process prior to providing any services.
        </Alert>
      )}
      <Paper elevation={1} sx={{ display: "grid", gridTemplateRows: "auto auto 1fr", height: "100%", width: "100%" }}>
        <Stack direction="row" flexWrap="wrap" gap={1} overflow="auto" p={1}>
          <ToggleButtonGroup
            onChange={(_e, newView) => {
              if (newView) setView(newView);
            }}
            size="small"
            value={view}
            exclusive
          >
            <Tooltip title="Map View">
              <ToggleButton sx={{ py: 0.5 }} value="map">
                <PublicIcon />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Table View">
              <ToggleButton sx={{ py: 0.5 }} value="table">
                <TableViewIcon />
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
          <Divider orientation="vertical" flexItem />
          <AddressPicker
            disabled={isLoading}
            filterKey="serviceAddress"
            filters={searchFilters}
            label="Service Address"
            onAddressPicked={(option, geoFeature) => {
              setMapViewState({
                location: {
                  option,
                  coordinates: geoFeature?.geometry?.coordinates,
                },
              });
            }}
            setFilters={setSearchFilters}
            sx={{
              flexGrow: 1,
              width: 0,
              minWidth: "15rem",
              position: "relative",
              ...(isLoading && {
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: theme.spacing(-0.5),
                  backgroundColor: alpha(theme.palette.common.black, 0.5),
                  zIndex: 1,
                  pointerEvents: "none",
                  borderTopLeftRadius: theme.shape.borderRadius,
                  borderBottomLeftRadius: theme.shape.borderRadius,
                },
              }),
            }}
            value={searchFilters.serviceAddress?.value}
          />
          <FilterAutocomplete
            disabled={isLoading}
            filterKey="services"
            filters={searchFilters}
            getChipStyle={() => ({
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
            })}
            label="Select Requested Service(s)"
            limitTags={2}
            options={useMemo(() => SERVICE_OPTIONS.map(([, label]) => label), [])}
            setFilters={setSearchFilters}
            sx={{
              flexGrow: 0,
              "& .MuiAutocomplete-inputRoot": {
                flexWrap: "nowrap",
                overflowX: "hidden",
              },
              position: "relative",
              ...(isLoading && {
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: theme.spacing(-0.5),
                  backgroundColor: alpha(theme.palette.common.black, 0.5),
                  zIndex: 1,
                  pointerEvents: "none",
                },
              }),
            }}
            noAutoselectAll
          />
          <Button
            disabled={isLoading || !!mapViewState.currentSearch || !searchFilters.serviceAddress || !searchFilters.services.length}
            onClick={() => handleSearch()}
            size="small"
            startIcon={<SearchIcon />}
            sx={{
              height: "initial",
              position: "relative",
              ...(isLoading && {
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: theme.spacing(-0.5),
                  backgroundColor: alpha(theme.palette.common.black, 0.5),
                  zIndex: 1,
                  pointerEvents: "none",
                  borderTopRightRadius: theme.shape.borderRadius,
                  borderBottomRightRadius: theme.shape.borderRadius,
                },
              }),
            }}
          >
            Find Providers
          </Button>
        </Stack>
        <Divider />
        <Box
          overflow="hidden"
          position="relative"
          sx={{
            "> div": {
              position: "absolute",
              inset: 0,
            },
          }}
        >
          {!!isLoading && (
            <>
              <Backdrop sx={{ zIndex: theme.zIndex.drawer + 1, borderRadius: 1 }} open />
              <Box
                sx={{
                  justifySelf: "center",
                  alignContent: "center",
                  zIndex: theme.zIndex.drawer + 1,
                }}
              >
                <CircularProgress size={150} />
              </Box>
            </>
          )}
          {hasError ? (
            <Box alignContent="center" justifyItems="center">
              <Typography>An error occurred while searching for providers, please try again</Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={[
                  view !== "map" && {
                    visibility: "hidden",
                  },
                ]}
                width="100%"
              >
                {mapView}
              </Box>
              <Box
                p={1}
                sx={[
                  view !== "table" && {
                    visibility: "hidden",
                  },
                ]}
                width="100%"
              >
                <TableView
                  hasCurrentSearch={!!mapViewState.currentSearch}
                  providers={mapViewState.providers}
                  setTableViewState={setTableViewState}
                  tableViewState={tableViewState}
                />
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </Stack>
  );
};

export default CareProvidersWrapper(CareProviders);
